import { differenceInMilliseconds } from "date-fns";
import { useCallback, useContext, useEffect, useRef, useState } from "react";

import axios from "../components/api/axios";
import { SESSION_WARNING_THRESHOLD_MS } from "../components/ui library/helpers/strings";
import DataContext from "../context/DataContext";
import { subscribeSessionWarning } from "../services/session-warning-channel";

import useLogout from "./useLogout";

const useForceLogout = () => {
  const { accessToken, setErrorMessage, stopSse } = useContext(DataContext);
  const [forceLogout, setForceLogout] = useState(false);
  const [warningDuration, setWarningDuration] = useState(SESSION_WARNING_THRESHOLD_MS);
  const intervalId = useRef(null);
  const attemptedRefresh = useRef(false); // Track whether we've already retried after missing token_exp to avoid loops
  const isChecking = useRef(false); // Prevent overlapping verification/logout calls
  const logout = useLogout();

  const handleLogout = useCallback(
    async (errMsg = "Your login has expired, please log in again!") => {
      if (isChecking.current) {
        return;
      }
      isChecking.current = true;

      if (intervalId.current) {
        clearInterval(intervalId.current);
        intervalId.current = null;
      }

      try {
        if (typeof stopSse === "function") {
          stopSse();
        }
        await logout({ errorMessage: errMsg });
      } catch {
        setErrorMessage("An error occurred during logout. Please try again.");
      } finally {
        setForceLogout(false);
        setWarningDuration(SESSION_WARNING_THRESHOLD_MS);
        isChecking.current = false;
      }
    },
    [logout, setErrorMessage, stopSse]
  );

  const handleContinue = useCallback(async () => {
    if (isChecking.current) {
      return;
    }

    isChecking.current = true;
    try {
      if (!accessToken) {
        await handleLogout("Access token is missing or invalid.");
        return;
      }

      await axios.get("/api/users/me/");
      setForceLogout(false);
      setWarningDuration(SESSION_WARNING_THRESHOLD_MS);
    } catch {
      setErrorMessage("An error occurred during refreshing session. Please try again.");
      setForceLogout(true);
    } finally {
      isChecking.current = false;
    }
  }, [accessToken, handleLogout, setErrorMessage]);

  useEffect(() => {
    attemptedRefresh.current = false;
  }, [accessToken]);

  useEffect(() => {
    const unsubscribe = subscribeSessionWarning(({ expiry }) => {
      if (expiry) {
        const expirationDate = new Date(expiry);
        const remaining = differenceInMilliseconds(expirationDate, new Date());
        setWarningDuration(
          Number.isFinite(remaining) && remaining > 0 ? remaining : SESSION_WARNING_THRESHOLD_MS
        );
      } else {
        setWarningDuration(SESSION_WARNING_THRESHOLD_MS);
      }
      setForceLogout(true);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!accessToken) {
      if (intervalId.current) {
        clearInterval(intervalId.current);
        intervalId.current = null;
      }
      setForceLogout(false);
      setWarningDuration(SESSION_WARNING_THRESHOLD_MS);
      return;
    }

    const interval = window.setInterval(async () => {
      if (isChecking.current) {
        return;
      }

      try {
        const tokenExpiration = localStorage.getItem("token_exp");

        if (!tokenExpiration) {
          if (!attemptedRefresh.current) {
            attemptedRefresh.current = true;
            await handleContinue();
            return;
          }
          await handleLogout("Unable to verify session. Please log in again.");
          return;
        }

        attemptedRefresh.current = false;
        const expirationDate = new Date(tokenExpiration);

        // Invalid date handling
        if (Number.isNaN(expirationDate.getTime())) {
          await handleLogout("Invalid token expiration date. Please log in again.");
          return;
        }

        const remaining = differenceInMilliseconds(expirationDate, new Date());

        // Session has expired, must log out
        if (remaining <= 0) {
          await handleLogout();
          return;
        }

        // Show the modal if remaining time equals or is less than 2 minutes (120000 milliseconds)
        if (remaining <= SESSION_WARNING_THRESHOLD_MS) {
          setWarningDuration(remaining > 0 ? remaining : SESSION_WARNING_THRESHOLD_MS);
          setForceLogout(true);
          return;
        }

        // The expiration is not complete, so the modal should be closed or remain closed
        setForceLogout(false);
        setWarningDuration(SESSION_WARNING_THRESHOLD_MS);
      } catch {
        await handleLogout("An unexpected error occurred while checking your session.");
      }
    }, 1000); // Check every second
    intervalId.current = interval;

    return () => {
      clearInterval(interval);
      intervalId.current = null;
    };
  }, [accessToken]); // Re-evaluate when token change

  useEffect(() => {
    if (!forceLogout) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      handleLogout();
    }, warningDuration);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [forceLogout, warningDuration, handleLogout]);

  return {
    forceLogout,
    handleLogout,
    handleContinue,
    warningDuration,
  };
};

export default useForceLogout;
